import { ArrowLeft, Home, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';

export interface NoAccessPageProps {
    title?: string;
    description?: string;
}

/**
 * Full-page placeholder rendered when a route's permission gate denies
 * access. Used as the default fallback by `RequirePagePermissions`.
 */
export function NoAccessPage({
    title = 'No access',
    description = "You don't have permission to view this page. If you believe this is a mistake, please contact your administrator.",
}: NoAccessPageProps) {
    const navigate = useNavigate();

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate('/');
    };

    return (
        <div className="flex min-h-[60vh] w-full items-center justify-center p-6">
            <Card className="w-full max-w-md">
                <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                        <Lock className="h-7 w-7" aria-hidden />
                    </div>
                    <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
                    <p className="text-sm text-gray-600">{description}</p>
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                        <Button variant="secondary" onClick={handleBack} dataTestId="ram-no-access-back">
                            <ArrowLeft className="h-4 w-4" aria-hidden />
                            Go back
                        </Button>
                        <Button variant="primary" onClick={() => navigate('/')} dataTestId="ram-no-access-home">
                            <Home className="h-4 w-4" aria-hidden />
                            Go to home
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
